import { useEffect, useState } from "react";
import { BookOpen, Package, Phone, MessageCircle } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import LeadModal from "@/components/LeadModal";
import {
  WHATSAPP_PRESENTATION,
  WHATSAPP_CONVERSATION,
  EMAIL,
  PHONE,
  WEBSITE,
  LOGO,
  TAGLINE,
} from "@/lib/sections";

export default function Contact() {
  const [modal, setModal] = useState(null);

  // The top-nav "Request Samples" button fires this event after scrolling here.
  useEffect(() => {
    const open = () => setModal("experience");
    window.addEventListener("aarya:request-samples", open);
    return () => window.removeEventListener("aarya:request-samples", open);
  }, []);

  return (
    <section
      id="contact"
      data-testid="section-contact"
      className="deck-section relative bg-ink"
    >
      <div className="mx-auto flex min-h-screen w-full max-w-[1400px] flex-col justify-center px-6 md:px-12 lg:px-16 py-28 md:py-28">
        {/* Header — centered max width to balance with CTAs below */}
        <div className="max-w-4xl">
          <Reveal>
            <div className="num-overline">11 · An Invitation</div>
          </Reveal>

          <Reveal delay={0.1}>
            <h2 className="mt-7 font-heading text-ivory text-4xl md:text-5xl lg:text-[60px] leading-[1.02] tracking-tightest">
              Let's build this category{" "}
              <span className="italic text-gold">together.</span>
            </h2>
          </Reveal>

          <Reveal delay={0.25}>
            <p className="mt-6 font-body text-[15px] md:text-base leading-[1.85] text-beige/75 max-w-2xl">
              An invitation to a curated, relationship-driven partnership with
              Aarya24KT — for international retailers, distributors and luxury
              hospitality.
            </p>
          </Reveal>
        </div>

        {/* CTAs — uniform 2×2 grid, expanded width, intriguing hover */}
        <Reveal delay={0.35}>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5 w-full max-w-[1100px]">
            <CtaCard
              num="01"
              label="Request Catalog"
              hint="We'll share a curated catalog based on your market and store type."
              action="Open form"
              icon={BookOpen}
              primary
              onClick={() => setModal("collection")}
              testId="cta-request-collection"
            />
            <CtaCard
              num="02"
              label="Request Samples"
              hint="Complimentary samples to evaluate quality and designs."
              action="Open form"
              icon={Package}
              onClick={() => setModal("experience")}
              testId="cta-experience-collection"
            />
            <CtaCard
              num="03"
              label="Schedule a Call"
              hint="On WhatsApp · time slots shared on request."
              action="Open WhatsApp"
              icon={Phone}
              href={WHATSAPP_PRESENTATION}
              testId="cta-schedule-presentation"
            />
            <CtaCard
              num="04"
              label="WhatsApp Us"
              hint="Direct conversation with our team."
              action="Open WhatsApp"
              icon={MessageCircle}
              href={WHATSAPP_CONVERSATION}
              testId="cta-start-conversation"
            />
          </div>
        </Reveal>

        <Reveal delay={0.6}>
          <p className="mt-7 num-overline text-ivory/40 text-[10px]">
            Available for international retail and distribution partners.
          </p>
        </Reveal>

        {/* CONTACT — a proper bounded "end of presentation" card */}
        <Reveal delay={0.8}>
          <div className="mt-14 md:mt-16 relative border border-gold/30 bg-coal/40 backdrop-blur-sm w-full max-w-[1100px]">
            {/* Corner accents to mark "end of deck" */}
            <span className="absolute -top-px -left-px h-6 w-6 border-t border-l border-gold" />
            <span className="absolute -top-px -right-px h-6 w-6 border-t border-r border-gold" />
            <span className="absolute -bottom-px -left-px h-6 w-6 border-b border-l border-gold" />
            <span className="absolute -bottom-px -right-px h-6 w-6 border-b border-r border-gold" />

            <div className="p-7 md:p-10">
              {/* Top row — End mark + Logo */}
              <div className="flex items-center justify-between gap-6 pb-6 md:pb-8 border-b border-gold/15">
                <div className="num-overline text-gold/85">End of Presentation</div>
                <div className="flex items-center gap-3">
                  <img
                    data-testid="footer-logo"
                    src={LOGO.mark}
                    alt="Aarya24KT"
                    crossOrigin="anonymous"
                    className="h-9 md:h-10 w-auto object-contain opacity-95"
                  />
                </div>
              </div>

              {/* Identity + contact rows */}
              <div className="mt-7 md:mt-8 grid grid-cols-1 lg:grid-cols-12 gap-7 lg:gap-10 items-start lg:items-end">
                <div className="lg:col-span-5">
                  <div className="num-overline text-gold/80">International Partnerships</div>
                  <div className="mt-3 font-heading text-ivory text-3xl md:text-[44px] tracking-tightest leading-none">
                    Manika S
                  </div>
                  <div className="mt-2 font-heading italic text-beige/65 text-sm md:text-base">
                    Aarya24KT · Mumbai
                  </div>
                </div>

                <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-7 lg:gap-10">
                  <ContactInline
                    label="Email"
                    value={EMAIL}
                    href={`mailto:${EMAIL}`}
                    testId="contact-email"
                  />
                  <ContactInline
                    label="Phone"
                    value={PHONE}
                    href={`tel:${PHONE.replace(/\s/g, "")}`}
                    testId="contact-phone"
                  />
                  <ContactInline
                    label="Website"
                    value={WEBSITE}
                    href={`https://${WEBSITE}`}
                    external
                    testId="contact-website"
                  />
                </div>
              </div>

              {/* Closing aphorism */}
              <div className="mt-9 md:mt-10 pt-7 md:pt-8 border-t border-gold/15 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <p className="font-heading italic text-gold/65 text-base md:text-lg max-w-xl">
                  "An object in gold is never just an object — it is a feeling,
                  gifted forward."
                </p>
                <span className="num-overline text-ivory/35 text-[10px] whitespace-nowrap">
                  {TAGLINE}
                </span>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={1.0}>
          <div className="mt-6 num-overline text-ivory/30 text-[10px]">
            © Aarya24KT · Mumbai · Crafted in 24KT
          </div>
        </Reveal>
      </div>

      {/* Modals */}
      <LeadModal
        open={modal === "collection"}
        onOpenChange={(v) => setModal(v ? "collection" : null)}
        title="Request Catalog."
        microcopy="We'll share a curated catalog based on your market and store type. A member of our team will be in touch."
        ctaSource="request_collection"
        confirmation="Thank you. Our team will share the catalog shortly."
      />
      <LeadModal
        open={modal === "experience"}
        onOpenChange={(v) => setModal(v ? "experience" : null)}
        title="Request Samples."
        microcopy="Selected samples will be shared as a complimentary offering from Aarya24KT for you to evaluate the quality and designs."
        ctaSource="experience_collection"
        showShipping
        confirmation="Our team will get in touch regarding samples."
      />
    </section>
  );
}

const CtaCard = ({ num, label, hint, action, icon: Icon, primary, onClick, href, testId }) => {
  const borderCls = primary
    ? "border-gold/70 hover:border-gold hover:bg-gold/[0.06]"
    : "border-ivory/20 hover:border-gold/70 hover:bg-gold/[0.04]";
  const numCls = primary ? "text-gold" : "text-ivory/55 group-hover:text-gold/90";
  const labelCls = primary ? "text-gold" : "text-ivory group-hover:text-gold";
  const iconCls = primary
    ? "text-gold/90"
    : "text-ivory/55 group-hover:text-gold";
  const actionCls = primary
    ? "text-gold/85"
    : "text-ivory/50 group-hover:text-gold";

  const content = (
    <div className="relative h-full px-7 md:px-9 py-7 md:py-9 flex flex-col justify-between min-h-[185px] cursor-pointer">
      <div className="flex items-start justify-between gap-4">
        <div
          className={`num-overline text-[10px] tracking-[0.32em] transition-colors duration-500 ${numCls}`}
        >
          {num}
        </div>
        {Icon && (
          <Icon
            className={`h-[20px] w-[20px] transition-all duration-500 ease-luxe ${iconCls} group-hover:-rotate-3`}
            strokeWidth={1.25}
          />
        )}
      </div>

      <div>
        <div
          className={`font-body text-[14px] md:text-[15px] uppercase tracking-[0.26em] transition-all duration-500 ease-luxe group-hover:tracking-[0.32em] ${labelCls}`}
        >
          {label}
        </div>
        <div className="mt-2.5 font-body text-[12.5px] md:text-[13px] leading-[1.6] text-beige/60 group-hover:text-beige/85 transition-colors duration-500 max-w-[36ch]">
          {hint}
        </div>

        {action && (
          <div
            className={`mt-4 inline-flex items-center gap-3 font-heading italic text-[13px] md:text-[14px] transition-all duration-500 ease-luxe ${actionCls}`}
          >
            <span className="block h-[1px] w-5 bg-current opacity-60 transition-all duration-500 ease-luxe group-hover:w-9" />
            <span>{action}</span>
          </div>
        )}
      </div>

      {/* Signature gold hairline that draws on hover */}
      <span
        className="pointer-events-none absolute left-0 bottom-0 h-[2px] bg-gold w-0 group-hover:w-full transition-[width] duration-[900ms] ease-luxe"
      />
    </div>
  );

  const baseCls = `group relative block w-full text-left border bg-transparent cursor-pointer transition-colors duration-500 ease-luxe ${borderCls}`;

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        data-testid={testId}
        className={baseCls}
      >
        {content}
      </a>
    );
  }
  return (
    <button onClick={onClick} data-testid={testId} className={baseCls}>
      {content}
    </button>
  );
};

const ContactInline = ({ label, value, href, external, testId }) => (
  <a
    href={href}
    target={external ? "_blank" : undefined}
    rel={external ? "noreferrer" : undefined}
    data-testid={testId}
    className="group flex flex-col border-b border-gold/15 pb-3 hover:border-gold/50 transition-colors duration-500"
  >
    <span className="num-overline text-ivory/45 text-[10px]">{label}</span>
    <span className="mt-1.5 font-body text-ivory text-[13px] md:text-[14px] group-hover:text-gold transition-colors duration-500 whitespace-nowrap">
      {value}
    </span>
  </a>
);
